import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ListSpinner } from '../../adminComponents/AdminSpinner/adminSpinner';
import { NotificationBellIcon } from '../../assets/svg';
import { DropdownMenuComponent } from '../../components/dropdownMenu/dropdownMenuComponent';
import { ADMIN_NOTIFICATIONS } from '../../routes/adminConstants';
import {
  HeaderIconWrapper,
  Overlay,
  overlayAnimationVariant,
} from '../../styles/styles';
import type { NotificationEntity } from '../../types/notification.types';
import { useIsBellScreen } from './bellScreens';
import { NotificationRow } from './components/NotificationRow';
import { formatUnreadBadge } from './helpers/notificationFormat';
import { useNotificationActions } from './hooks/useNotificationActions';
import { useNotificationsList } from './hooks/useNotificationsList';
import { useNotificationsUnreadCount } from './hooks/useNotificationsUnreadCount';
import { useNotificationsViewer } from './hooks/useNotificationsViewer';
import { TextAction } from './notificationActions.styled';
import {
  BellAnchor,
  BellGlyph,
  CountBadge,
  Panel,
  PanelFooter,
  PanelHeader,
  PanelList,
  PanelNote,
  PanelTitle,
} from './NotificationsBell.styled';

/** How many the dropdown shows; the rest are one click away on See all. */
const DROPDOWN_LIMIT = 10;

/**
 * The bell in the page header, for the users who have one and on the screens
 * that wear it (`bellScreens`) - nothing at all everywhere else, including the
 * apps without notifications.
 */
export const NotificationsBell = () => {
  const { enabled } = useNotificationsViewer();
  const isBellScreen = useIsBellScreen();
  return enabled && isBellScreen ? <BellMenu /> : null;
};

const BellMenu = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const knownUnread = useNotificationsUnreadCount();
  const unread = knownUnread ?? 0;
  const ringing = useRingOnIncrease(knownUnread);

  /* Fetched on the first open, not with the page: the header is on every
     screen, and most visits never open it. */
  const list = useNotificationsList({ enabled: open });
  const actions = useNotificationActions();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const openNotification = (notification: NotificationEntity) => {
    setOpen(false);
    actions.open(notification);
  };

  const seeAll = () => {
    setOpen(false);
    navigate(ADMIN_NOTIFICATIONS);
  };

  const rows = list.items.slice(0, DROPDOWN_LIMIT);

  return (
    <>
      <BellAnchor>
        <HeaderIconWrapper
          $opened={open}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
          }
          onClick={() => setOpen((value) => !value)}
        >
          <BellGlyph $ringing={ringing}>
            <NotificationBellIcon />
          </BellGlyph>
        </HeaderIconWrapper>

        {unread > 0 && (
          <CountBadge aria-hidden="true">
            {formatUnreadBadge(unread)}
          </CountBadge>
        )}

        <DropdownMenuComponent isOpen={open} toLeft padding="0">
          <Panel id={panelId} role="dialog" aria-label="Notifications">
            <PanelHeader>
              <PanelTitle>Notifications</PanelTitle>
              {unread > 0 && (
                <TextAction
                  type="button"
                  onClick={actions.markAll}
                  disabled={actions.markingAll}
                >
                  Mark all as read
                </TextAction>
              )}
            </PanelHeader>

            <PanelList>
              {list.isLoading && <ListSpinner loading mb="0" mbSM="0" />}

              {list.isError && rows.length === 0 && (
                <PanelNote>
                  Notifications could not be loaded.{' '}
                  <TextAction type="button" onClick={() => list.refetch()}>
                    Try again
                  </TextAction>
                </PanelNote>
              )}

              {!list.isLoading && !list.isError && rows.length === 0 && (
                <PanelNote>You are all caught up.</PanelNote>
              )}

              {rows.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onOpen={openNotification}
                  compact
                />
              ))}
            </PanelList>

            <PanelFooter>
              <TextAction type="button" onClick={seeAll}>
                See all notifications
              </TextAction>
            </PanelFooter>
          </Panel>
        </DropdownMenuComponent>
      </BellAnchor>

      {open && (
        <Overlay
          key="notificationsOverlay"
          aria-hidden="true"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setOpen(false)}
        />
      )}
    </>
  );
};

/**
 * True for one swing whenever the count goes UP - a new notification - and
 * never for the first known value, or every page load would ring.
 */
const useRingOnIncrease = (count: number | undefined) => {
  const previous = useRef<number | undefined>(undefined);
  const [ringing, setRinging] = useState(false);

  useEffect(() => {
    const before = previous.current;
    previous.current = count;
    if (before === undefined || count === undefined || count <= before) {
      /* A change mid-swing cancelled the timer that would have ended it. */
      setRinging(false);
      return undefined;
    }
    setRinging(true);
    const timer = setTimeout(() => setRinging(false), 800);
    return () => clearTimeout(timer);
  }, [count]);

  return ringing;
};
